import {Box, Grid2, IconButton, Stack, Tab, Tabs, Typography} from "@mui/material";
import * as React from "react";
import {ComponentType, SVGProps} from "react";

import {
    ArrowLeftSvg,
    ArrowRightSvg,
    BulletGreenSvg,
    BulletWhiteSvg,
    Flexible1Svg,
    Flexible2Svg,
    Flexible3Svg,
    Focus1Svg,
    Focus2Svg,
    Focus3Svg,
    Growth1Svg,
    Growth2Svg,
    Growth3Svg
} from "@/pages/landing-page/utils/Svgs";
import {CrossfadeBox} from "@/pages/landing-page/utils/CrossfadeBox";
import {CooperationSlide} from "@/pages/landing-page/utils/CooperationSlide";
import {Section2DTO} from "@/pages/landing-page/landing-page-data";
import {compactValue} from "@/lib/screen-class";
import {LANDING_HEADLINE} from "@/pages/landing-page/utils/landing-font-sizes";
import {COMPACT_SECTION_MIN_HEIGHT} from "@/pages/landing-page/utils/landing-section-layout";

const color_coop = ["rgb(224, 239, 172)", "rgb(200, 179, 255)", "rgb(205, 223, 242)"];

// Illustration sets per cooperation slide (index-aligned with data.cooperation).
const cooperationSvgs: ComponentType<SVGProps<SVGSVGElement>>[][] = [
    [Focus1Svg, Focus2Svg, Focus3Svg],
    [Flexible1Svg, Flexible2Svg, Flexible3Svg],
    [Growth1Svg, Growth2Svg, Growth3Svg]
];

interface Section2Props {
    data: Section2DTO;
}

/** Font size of a tab label below `sm`, by the length of the longest word across the tab labels. */
const tabFontSizeBelowSm = (longestWord: number): string => {
    if (longestWord > 13) {
        return "11px";
    }
    return longestWord > 12 ? "12px" : "14px";
};

export const Section2 = ({ data }: Section2Props) => {
    const [index, setIndex] = React.useState(0);
    const length = data.tabs.length;
    const longestTabWord = Math.max(...data.tabs.flatMap((tab) => tab.split(/\s+/)).map((word) => word.length));
    const prev = () => setIndex((prevIndex) => (prevIndex - 1 + length) % length);
    const next = () => setIndex((prevIndex) => (prevIndex + 1) % length);

    return (
        <Grid2
            container
            direction="column"
            sx={{
                minHeight: COMPACT_SECTION_MIN_HEIGHT,
                // backgroundColor: color_grey[4],
                position: "relative",
                width: "100%",
                paddingTop: 4,
                px: {
                    md: 1,
                    lg: 10
                }
            }}
        >
            <Grid2 width="100%" paddingBottom={4}>
                <Typography
                    fontWeight="700"
                    width="fit-content"
                    lineHeight={1}
                    paddingBottom={2}
                    sx={{ fontSize: LANDING_HEADLINE }}
                >
                    {data.title}:
                </Typography>
                <Typography width="fit-content" lineHeight={1} sx={{ fontSize: LANDING_HEADLINE }}>
                    {data.subTitle}
                </Typography>
            </Grid2>

            <Grid2
                // width="100%"
                sx={{
                    paddingTop: {
                        xs: 0,
                        sm: 0,
                        md: 5,
                        lg: 10,
                        xl: 10
                    }
                }}
            >
                <Tabs
                    variant="fullWidth"
                    value={index}
                    onChange={(e, v) => setIndex(v)}
                    sx={{
                        "&  button": {
                            // Below `sm` a smaller font and padding, so "Flexibilität" fits its third of a 360 px
                            // window in one line; MUI's tab padding from `md`.
                            // A word longer than "Flexibilität" (pl "bezpieczeństwo", fr "Concentration") cannot
                            // wrap, so that language gets a slightly smaller font below `sm` instead of a clipped tab.
                            fontSize: {
                                xs: tabFontSizeBelowSm(longestTabWord),
                                sm: "18px",
                                md: "24px"
                            },
                            paddingX: { xs: 0.5, sm: 1, md: 2 }
                        },
                        // "& .MuiTabs-indicator": {
                        //     backgroundColor: color_coop[index]
                        // },
                        // Below `md` a smaller gap, so the three tabs fit a 360 px window; their labels wrap.
                        "&>div>div": {
                            gap: compactValue(1, 6)
                        }
                    }}
                >
                    {data.tabs.map((label, tabIndex) => (
                        <Tab
                            key={label}
                            // A wrapped label keeps "&" with the word after it instead of on a line of its own.
                            label={label.replaceAll("& ", "& ")}
                            value={tabIndex}
                            style={{
                                fontWeight: "bold",
                                borderBottom: "2.5px solid #000080",
                                textTransform: "none",
                                backgroundColor: index === tabIndex ? color_coop[tabIndex] : undefined
                            }}
                        />
                    ))}
                </Tabs>
            </Grid2>
            <Grid2
                sx={{
                    paddingTop: {
                        xs: 0,
                        sm: 0,
                        md: 4
                    }
                }}
                width="100%"
            >
                <Box>
                    <CrossfadeBox idx={index}>
                        {data.cooperation.map((messages, slideIndex) => (
                            <CooperationSlide
                                key={data.tabs[slideIndex]}
                                svgs={cooperationSvgs[slideIndex]}
                                borderColor={color_coop[slideIndex]}
                                messages={messages}
                            />
                        ))}
                    </CrossfadeBox>
                </Box>
            </Grid2>
            <Grid2 container justifyContent="center" width="100%">
                <Stack
                    data-cy="cooperation-pager"
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="center"
                    paddingTop={3}
                >
                    <IconButton onClick={prev} size="small">
                        <ArrowLeftSvg />
                    </IconButton>

                    <Stack direction="row">
                        {data.tabs.map((tabLabel, ci) => (
                            <IconButton key={tabLabel} size="small" onClick={() => setIndex(ci)}>
                                {ci === index ? <BulletGreenSvg /> : <BulletWhiteSvg />}
                            </IconButton>
                        ))}
                    </Stack>

                    <IconButton onClick={next} size="small">
                        <ArrowRightSvg />
                    </IconButton>
                </Stack>
            </Grid2>
        </Grid2>
    );
};
